/**
 * CallCard Component
 *
 * Inline call card for incoming/active calls with SAP contact linking and transfer/conference panels.
 */

import { useCallback } from 'react';
import { useCallTimer } from '../../hooks/useCallTimer';
import { useCallControls } from '../../hooks/useCallControls';
import { useSapContactActions } from '../../hooks/useSapContactActions';
import { getPendingInboundContactId } from '../../services/cxone-sdk.service';
import { TransferPanel } from './TransferPanel';
import { CallActiveControls } from './CallActiveControls';
import { CallCardHeader } from './CallCardHeader';
import { CallCardSapSections } from './CallCardSapSections';
import { CallHoldControls } from './CallHoldControls';
import { CallTimerDisplay } from './CallTimerDisplay';
import { CallIncomingActions } from './CallIncomingActions';
import { CallEndAction } from './CallEndAction';
import { CallCardProps, resolveCallDirection } from './call-card.types';
import { useCallCardPanel } from './useCallCardPanel';
import { useCallCardRingtone } from './useCallCardRingtone';
import styles from './CallCard.module.css';

export type { ManualSearchState } from './sap-contact.types';

export function CallCard({
  callState,
  isRinging,
  isMuted: initialMuted,
  isAgentLegActive = true,
  callError,
  callDisplayContext,
  sapContactState,
  manualSearchState,
  onAccept,
  onReject,
  onTransfer,
  onConference,
  onCallEnd,
  onContactOpened,
  onManualSearch,
  linkedSapContactId = null,
}: CallCardProps) {
  const { audioRef, stopRingtone } = useCallCardRingtone(isRinging);

  const callControlContactId = callState.contactId ?? getPendingInboundContactId();

  const { isMuted, isLoading, handleHold, handleResume, handleMute, handleEnd } = useCallControls({
    contactId: callControlContactId,
    initialMuted,
    onCallEnd,
  });

  const { handleLinkContact } = useSapContactActions({
    callerNumber: callState.callerNumber,
    linkedSapContactId,
    callDirection: resolveCallDirection(callState, callDisplayContext),
    preferredReferenceId: callDisplayContext?.outboundReferenceId,
    onContactOpened,
  });

  const { formattedDuration } = useCallTimer(callState.startTime);

  const {
    panelMode,
    isPanelOpen,
    handleTransferClick,
    handleConferenceClick,
    handlePanelClose,
    handlePanelComplete,
  } = useCallCardPanel(onTransfer, onConference);

  const handleAccept = useCallback(() => {
    stopRingtone();
    onAccept();
  }, [stopRingtone, onAccept]);

  const handleReject = useCallback(() => {
    stopRingtone();
    onReject();
  }, [stopRingtone, onReject]);

  const isOnHold = callState.status === 'holding';
  const isConnecting = callState.status === 'connecting';
  const isActive = callState.status === 'active';
  const showLimitedCallControls = isConnecting;

  return (
    <div className={styles.callCard}>
      <audio ref={audioRef} src="/sounds/ringtone.mp3" />

      <CallCardHeader
        callState={callState}
        callDisplayContext={callDisplayContext}
        isRinging={isRinging}
        isOnHold={isOnHold}
        isActive={isActive}
        isConnecting={isConnecting}
        callError={callError}
      />

      <CallCardSapSections
        callState={callState}
        sapContactState={sapContactState}
        manualSearchState={manualSearchState}
        linkedSapContactId={linkedSapContactId}
        onLink={handleLinkContact}
        onManualSearch={onManualSearch}
      />

      {isRinging && (
        <CallIncomingActions
          callState={callState}
          isLoading={isLoading}
          onAccept={handleAccept}
          onReject={handleReject}
        />
      )}

      {showLimitedCallControls && (
        <CallEndAction isLoading={isLoading} disabled={!callControlContactId} onEnd={handleEnd} />
      )}

      {isActive && !isRinging && (
        <CallActiveControls
          isMuted={isMuted}
          isOnHold={isOnHold}
          isLoading={isLoading}
          isAgentLegActive={isAgentLegActive}
          panelMode={panelMode}
          isPanelOpen={isPanelOpen}
          formattedDuration={formattedDuration}
          onMute={handleMute}
          onHold={handleHold}
          onResume={handleResume}
          onEnd={handleEnd}
          onTransferClick={handleTransferClick}
          onConferenceClick={handleConferenceClick}
        />
      )}

      {isOnHold && !isRinging && (
        <>
          <CallTimerDisplay formattedDuration={formattedDuration} />
          <CallHoldControls isLoading={isLoading} onResume={handleResume} onEnd={handleEnd} />
        </>
      )}

      {isPanelOpen && callState.contactId && (
        <TransferPanel
          mode={panelMode!}
          contactId={callState.contactId}
          onClose={handlePanelClose}
          onComplete={handlePanelComplete}
        />
      )}
    </div>
  );
}

export default CallCard;
